import { DeeplyReadonly, Filter, Query } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { DescriptionOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, Link } from '@mui/material';
import { Operator } from '@procureai/query-builder';
import {
  formatNumber,
  LinearProgressChart,
  LinearProgressList,
  LinearProgressListItem,
  LinearProgressListSubheader,
  NumberFormatPreset,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isNil, isEmpty } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { PurchaseRequisitionFilterField } from '@/generated/gql/graphql';
import { BaseDomainType, I18nNamespace } from '~/common/types';
import { PurchaseRequisitionStatus } from '~/common/types/PurchaseRequisitionStatus';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { EncodedQuery } from '~/components/EncodedQuery/EncodedQuery';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { VirtualList, VirtualListProps } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';
import { RouteId } from '~/routes';

const MAX_ITEMS = 4;

const tooltipFormatter = formatNumber(NumberFormatPreset.CURRENCY);

export const OpenPurchaseRequisitionsWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'OpenPurchaseRequisitionsWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'open-purchase-requisitions' });
  const [dialogProps, dialogOpen] = useOpenClose();

  const cube = 'PurchaseRequisition';

  const filters: Filter[] = [
    {
      member: `${cube}.supplierId`,
      operator: 'equals',
      values: [entityId],
    },
    {
      member: `${cube}.status`,
      operator: 'equals',
      values: [PurchaseRequisitionStatus.OPEN],
    },
  ];

  const request: DeeplyReadonly<Query> = {
    dimensions: [
      `${cube}.id`,
      `${cube}.externalId`,
      `${cube}.summary`,
      `${cube}.value`,
      `${cube}.totalValue`,
      `${cube}.count`,
    ],
    order: {
      [`${cube}.value`]: 'desc',
    },
    filters,
  };

  const query = useCubeQuery({ ...request, limit: MAX_ITEMS });

  const nodes = query.resultSet?.rawData()?.map((segment) => ({
    id: segment[`${cube}.id`],
    sourceReference: segment[`${cube}.externalId`],
    description: segment[`${cube}.summary`],
    value: Number(segment[`${cube}.value`]) || 0,
    totalValue: Number(segment[`${cube}.totalValue`]) || 0,
    count: segment[`${cube}.count`],
  }));

  const isNodesEmpty = isEmpty(nodes) || isNil(nodes);
  const isLoading = query.isLoading;
  const purchaseRequisitionCount = parseInt(nodes?.[0]?.count) || 0;
  const purchaseRequisitionIds = nodes?.map((item) => item.id);

  const hasItems = !!purchaseRequisitionCount;
  const hasMore = purchaseRequisitionCount > MAX_ITEMS;
  const showInBaseCount = nodes ? (nodes.length < MAX_ITEMS ? nodes.length : MAX_ITEMS) : 0;

  const rules = useMemo(() => {
    return [{ field: PurchaseRequisitionFilterField.id, value: purchaseRequisitionIds, operator: Operator.IN }];
  }, [purchaseRequisitionIds]);

  const renderItem: NonNullable<VirtualListProps<any>['renderItem']> = useMemo(
    () =>
      ({ item }: { item: any }) => {
        const { id, sourceReference, description, value, totalValue } = item;
        const barValue = value;
        const barMax = totalValue;
        return (
          <Link key={id} component={RouterLink} to={RouteId.purchaseRequisitionDetail(id)} underline="none">
            <Box
              style={{ listStyleType: 'none' }}
              sx={{
                py: 4,
              }}
            >
              <LinearProgressListItem
                key={id}
                component="div"
                primary={sourceReference}
                secondary={description}
                primaryRight={t(!totalValue ? 'value' : 'valueTotal', {
                  value,
                  totalValue,
                })}
              >
                {barValue !== null && barMax !== null && (
                  <LinearProgressChart value={barValue} max={barMax} tooltipFormatter={tooltipFormatter} />
                )}
              </LinearProgressListItem>
            </Box>
          </Link>
        );
      },
    [t],
  );

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="OpenPurchaseRequisitionsWidget">
        <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />} />
        <WidgetContent>
          {!isNodesEmpty ? (
            <LinearProgressList
              items={nodes}
              renderItem={renderItem}
              subheader={
                hasItems ? (
                  <LinearProgressListSubheader disableGutters disableSticky>
                    {t('listSubheader')}
                  </LinearProgressListSubheader>
                ) : undefined
              }
            />
          ) : isLoading ? (
            <CircularProgressCentered sx={{ position: 'relative' }} />
          ) : (
            <PlaceholderWidget />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore || isNodesEmpty} onClick={dialogOpen}>
            {t('showAll', { count: purchaseRequisitionCount || 0 })}
          </Button>
          <EncodedQuery pathname={RouteId.analyticsBase(BaseDomainType.PURCHASE_REQUISITION)} rules={rules}>
            {(linkProps) => (
              <Button variant="text" disabled={!hasItems} {...linkProps}>
                {t('showInBase', { count: showInBaseCount })}
              </Button>
            )}
          </EncodedQuery>
        </WidgetFooter>
      </Widget>
      <Dialog
        {...dialogProps}
        sx={{
          '& .MuiDialog-paper': {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent>
          <Widget>
            <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />} />
            <WidgetContent>
              <Box
                sx={{
                  marginBottom: 16,
                }}
              >
                <VirtualList
                  itemSize={88}
                  query={request}
                  queryOptions={{
                    skip: !dialogProps.open,
                    segmentMapper: (segment) => ({
                      id: segment[`${cube}.id`],
                      sourceReference: segment[`${cube}.externalId`],
                      description: segment[`${cube}.summary`],
                      value: Number(segment[`${cube}.value`]) || 0,
                      totalValue: Number(segment[`${cube}.totalValue`]) || 0,
                      count: segment[`${cube}.count`],
                    }),
                    variables: {
                      id: entityId,
                    },
                  }}
                  renderItem={renderItem}
                />
              </Box>
            </WidgetContent>
          </Widget>
        </DialogContent>
      </Dialog>
    </>
  );
};
